"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";

import { ErrorMessage } from "@/components/api-error";
import CommentInput from "@/components/comment-input";
import PostDetailsSkeleton from "@/components/loading-skeletons/post-details-loading";
import PostDetailsPage from "@/components/posts/post-details";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { postDetails } from "@/lib/apis/posts";
import { parseApiError } from "@/lib/error-handler";
import { useStore } from "@/store/store";

type PostModalProps = {
  id: string | number;
};

export default function PostModal({ id }: PostModalProps) {
  const queryClient = useQueryClient();
  const { setSelectedPostId } = useStore();

  const {
    data,
    isLoading,
    error,
    isError,
  } = useQuery<PostDetailsResponse>({
    queryKey: ["post-details"],
    queryFn: () => postDetails(id),
    enabled: !!id,
  });

  const post = data?.data.post;

  const handleClose = () => {
    queryClient.removeQueries({ queryKey: ["post-details", id] });
    setSelectedPostId(null);
  };

  return (
    <Dialog defaultOpen onOpenChange={o => !o && handleClose()}>
      <DialogTitle className=" sr-only">Post Moda</DialogTitle>
      <DialogContent
        className="max-w-[700px] h-[90dvh] p-0 flex flex-col"
        isShowCloseModal={false}
      >
        <div className="sticky top-0 flex items-center justify-between border-b bg-background px-4 py-3 z-10">
          <div />
          <h2 className="text-lg font-semibold">
            {isLoading ? "Loading..." : `${post?.user?.fullName} Post`}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 rounded-full hover:bg-accent cursor-pointer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {
          isLoading
            ? <PostDetailsSkeleton />
            : isError
              ? (
                <div className="flex-1 flex justify-center items-center p-4">
                  <ErrorMessage type={parseApiError(error).errorType} message={parseApiError(error).message} />
                </div>
              )
              : (
                post && (
                  <>
                    <div className="flex-1 overflow-y-auto px-4 py-3 scrollbar-hide">
                      <PostDetailsPage post={post} />
                    </div>

                    <div className="px-4 py-3">
                      <CommentInput post={post} />
                    </div>
                  </>
                )
              )
        }
      </DialogContent>
    </Dialog>
  );
}
